import React, { useEffect, useMemo, useState } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { TrashIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { formatMeterRaw, formatTradingDay } from './dailyDataUtils'
import { meterCentsToDollars } from '../../utils/gamingCurrency'

const METRICS = [
  {
    id: 'turnover',
    label: 'Turnover',
    egmTitle: 'By EGM',
    hourTitle: 'By hour',
    hint: 'Wagered, including replayed wins',
    color: '#4f46e5',
  },
  {
    id: 'cashin',
    label: 'Cash in',
    egmTitle: 'By EGM',
    hourTitle: 'By hour',
    hint: 'Notes into the acceptor',
    color: '#0f766e',
  },
  {
    id: 'cashlessout',
    label: 'Ticket out',
    egmTitle: 'By EGM',
    hourTitle: 'By hour',
    hint: 'Off the machine, not cage redemption',
    color: '#d97706',
  },
  {
    id: 'cancelcredit',
    label: 'Cancel credit',
    egmTitle: 'By EGM',
    hourTitle: 'By hour',
    hint: 'Attendant handpays',
    color: '#e11d48',
  },
]

const MIN_PRESETS = [
  { id: 'all', label: 'All', lo: 0 },
  { id: '1k', label: '≥ $1k', lo: 1000 },
  { id: '5k', label: '≥ $5k', lo: 5000 },
  { id: '10k', label: '≥ $10k', lo: 10000 },
  { id: '20k', label: '≥ $20k', lo: 20000 },
]

const MACHINE_CLASS_OPTIONS = [
  { id: 'all', label: 'All types' },
  { id: 'EGM', label: 'EGM' },
  { id: 'MTGM', label: 'MTGM' },
]

function machineClassLabel(option, counts) {
  if (!counts || option.id === 'all') return option.label
  const n = option.id === 'EGM' ? counts.EGM : counts.MTGM
  return n ? `${option.label} · ${n}` : option.label
}

function egmLabelFor(egmpk, charts) {
  const row = (charts?.by_egm || []).find((e) => String(e.egmpk) === String(egmpk))
  return row?.house_number || row?.egmpk || egmpk
}

function FocusFilterChips({
  machineClass,
  selectedEgmpks,
  selectedHours,
  charts,
  onRemoveEgm,
  onRemoveHour,
  onClearFocus,
}) {
  const hasFocus = (selectedEgmpks?.length || 0) > 0 || (selectedHours?.length || 0) > 0
  if (!hasFocus) return null
  return (
    <div className="flex flex-wrap items-center gap-1.5 mt-2">
      <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">Focus</span>
      {selectedEgmpks.map((egmpk) => (
        <button
          key={`egm-${egmpk}`}
          type="button"
          onClick={() => onRemoveEgm?.(egmpk)}
          className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-900 hover:bg-indigo-100"
          title="Remove machine from focus"
        >
          {egmLabelFor(egmpk, charts)}
          <XMarkIcon className="w-3 h-3" />
        </button>
      ))}
      {selectedHours.map((hour) => (
        <button
          key={`hour-${hour}`}
          type="button"
          onClick={() => onRemoveHour?.(hour)}
          className="inline-flex items-center gap-1 rounded-full border border-teal-200 bg-teal-50 px-2 py-0.5 text-[11px] font-medium text-teal-900 hover:bg-teal-100"
          title="Remove hour from focus"
        >
          {String(hour).padStart(2, '0')}:00
          <XMarkIcon className="w-3 h-3" />
        </button>
      ))}
      <button
        type="button"
        onClick={onClearFocus}
        className="text-[11px] font-medium text-gray-600 hover:text-gray-900 underline underline-offset-2"
      >
        Clear focus
      </button>
      {machineClass && machineClass !== 'all' ? (
        <span className="text-[11px] text-gray-400">· type filter kept</span>
      ) : null}
    </div>
  )
}

function toAud(cents) {
  return meterCentsToDollars(cents) ?? 0
}

function niceCeiling(value) {
  if (!Number.isFinite(value) || value <= 0) return 1000
  const exp = Math.floor(Math.log10(value))
  const mag = 10 ** exp
  const n = value / mag
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10
  return step * mag
}

function stepFor(max) {
  if (max >= 100000) return 1000
  if (max >= 20000) return 100
  if (max >= 5000) return 50
  return 10
}

function formatAudCompact(value) {
  const n = Number(value)
  if (!Number.isFinite(n) || n === 0) return '$0'
  if (n >= 1000) return `$${(n / 1000).toFixed(n % 1000 === 0 || n >= 10000 ? 0 : 1)}k`
  return `$${Math.round(n)}`
}

function audTick(value) {
  const n = Number(value)
  if (!Number.isFinite(n) || n === 0) return '0'
  if (n >= 1000) return `$${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`
  return `$${Math.round(n)}`
}

function ChartTooltip({ active, payload, label, metric }) {
  if (!active || !payload?.length) return null
  const row = payload[0]?.payload || {}
  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-xs shadow">
      <div className="font-medium text-gray-900">{row.fullLabel || label}</div>
      {row.game_name ? <div className="text-gray-500 mt-0.5">{row.game_name}</div> : null}
      <div className="tabular-nums text-gray-800 mt-1">{formatMeterRaw(row[metric.id])}</div>
    </div>
  )
}

function DualAudRange({ min, max, lo, hi, step, color, onChange }) {
  const span = max - min || 1
  const loPct = ((lo - min) / span) * 100
  const hiPct = ((hi - min) / span) * 100
  const loOnTop = lo > min + span * 0.5

  return (
    <div className="flex items-center gap-2 min-w-0">
      <span className="text-[11px] tabular-nums text-gray-500 w-12 shrink-0">{formatAudCompact(lo)}</span>
      <div className="relative h-5 flex-1 min-w-[120px]">
        <div className="absolute left-0 right-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-gray-200" />
        <div
          className="absolute top-1/2 h-1.5 -translate-y-1/2 rounded-full"
          style={{ left: `${loPct}%`, width: `${Math.max(0, hiPct - loPct)}%`, background: color }}
        />
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={lo}
          aria-label="Minimum amount"
          className="egm-aud-range"
          style={{ zIndex: loOnTop ? 3 : 2 }}
          onChange={(e) => {
            const next = Number(e.target.value)
            onChange(Math.min(next, hi), hi)
          }}
        />
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={hi}
          aria-label="Maximum amount"
          className="egm-aud-range"
          style={{ zIndex: loOnTop ? 2 : 3 }}
          onChange={(e) => {
            const next = Number(e.target.value)
            onChange(lo, Math.max(next, lo))
          }}
        />
      </div>
      <span className="text-[11px] tabular-nums text-gray-500 w-12 shrink-0 text-right">{formatAudCompact(hi)}</span>
    </div>
  )
}

export default function DailyTurnoverCharts({
  charts,
  tradingDay,
  amount,
  amountLoading,
  hasData,
  partialDay,
  allocateLabel,
  machineClass = 'all',
  machineClassCounts = null,
  onMachineClassChange,
  selectedEgmpks = [],
  selectedHours = [],
  onToggleEgm,
  onToggleHour,
  onClearFocus,
  onClear,
  clearing,
}) {
  const [metricId, setMetricId] = useState('turnover')
  const [range, setRange] = useState(null)
  const metric = METRICS.find((m) => m.id === metricId) || METRICS[0]
  const selectedEgmSet = useMemo(() => new Set((selectedEgmpks || []).map(String)), [selectedEgmpks])
  const selectedHourSet = useMemo(() => new Set(selectedHours || []), [selectedHours])
  const hasFocus = selectedEgmSet.size > 0 || selectedHourSet.size > 0

  useEffect(() => {
    setRange(null)
  }, [tradingDay, selectedEgmpks, selectedHours])

  const egmAll = useMemo(() => {
    const list = Array.isArray(charts?.by_egm) ? charts.by_egm : []
    return list
      .map((e) => ({
        ...e,
        label: e.house_number || e.egmpk,
        fullLabel: e.house_number || e.egmpk,
        aud: toAud(e[metric.id]),
      }))
      .filter((e) => e.aud > 0)
      .sort((a, b) => b.aud - a.aud)
  }, [charts, metric.id])

  const ceiling = useMemo(() => {
    const peak = egmAll.reduce((m, e) => Math.max(m, e.aud), 0)
    return niceCeiling(peak)
  }, [egmAll])

  const lo = range?.lo ?? 0
  const hiCap = range?.hi
  const hi = hiCap == null ? ceiling : Math.min(hiCap, ceiling)
  const isAll = lo <= 0 && hiCap == null
  const step = stepFor(ceiling)

  const egmRows = useMemo(() => {
    const maxAud = hiCap == null ? Infinity : hiCap
    return egmAll.filter((e) => e.aud >= lo && e.aud <= maxAud)
  }, [egmAll, lo, hiCap])

  const hourRows = useMemo(() => {
    const hours = Array.isArray(charts?.by_hour) ? charts.by_hour : []
    return hours.map((h) => ({
      ...h,
      fullLabel: h.label,
      aud: toAud(h[metric.id]),
    }))
  }, [charts, metric.id])

  const hasCharts = !!charts && METRICS.some((m) => {
    const egms = Array.isArray(charts.by_egm) ? charts.by_egm : []
    const hours = Array.isArray(charts.by_hour) ? charts.by_hour : []
    return egms.some((e) => (Number(e[m.id]) || 0) > 0) || hours.some((h) => (Number(h[m.id]) || 0) > 0)
  })
  const hasHour = hourRows.some((h) => h.aud > 0)
  const egmTickEvery = egmRows.length > 24 ? Math.ceil(egmRows.length / 16) : 1
  const presets = MIN_PRESETS.filter((p) => p.lo === 0 || p.lo < ceiling)

  function applyRange(nextLo, nextHi) {
    const clampedLo = Math.max(0, Math.min(Number(nextLo) || 0, ceiling))
    const unbounded = nextHi == null || Number(nextHi) >= ceiling
    if (clampedLo <= 0 && unbounded) setRange(null)
    else setRange({ lo: clampedLo, hi: unbounded ? null : Math.max(clampedLo, Number(nextHi) || 0) })
  }

  const rangeLabel = isAll
    ? `${egmAll.length} machines`
    : hiCap == null
      ? `${egmRows.length}/${egmAll.length} ≥ ${formatAudCompact(lo)}`
      : `${egmRows.length}/${egmAll.length} ${formatAudCompact(lo)}–${formatAudCompact(hiCap)}`

  const machineFilterActive = machineClass && machineClass !== 'all'
  const typeHint = machineFilterActive
    ? `${machineClass} only — totals and charts filtered`
    : hasFocus
      ? 'Focused view — click charts to add/remove machines or hours'
      : machineClassCounts?.unset
        ? `${machineClassCounts.unset} machine(s) without EGM/MTGM type in registry`
        : 'Click a machine or hour to focus · multi-select supported'

  function handleClearFocus() {
    setRange(null)
    onClearFocus?.()
  }

  function handleChartEgmClick(state) {
    const egmpk = state?.activePayload?.[0]?.payload?.egmpk
    if (egmpk != null) onToggleEgm?.(egmpk)
  }

  function handleChartHourClick(state) {
    const hour = state?.activePayload?.[0]?.payload?.hour
    if (hour != null) onToggleHour?.(hour)
  }

  const focusSubtitle = hasFocus
    ? [
        selectedEgmSet.size ? `${selectedEgmSet.size} machine${selectedEgmSet.size === 1 ? '' : 's'}` : null,
        selectedHourSet.size ? `${selectedHourSet.size} hour${selectedHourSet.size === 1 ? '' : 's'}` : null,
      ].filter(Boolean).join(' · ')
    : null

  return (
    <div className="bg-white shadow rounded-lg p-4">
      <style>{`
        .egm-aud-range {
          -webkit-appearance: none;
          appearance: none;
          background: transparent;
          pointer-events: none;
          position: absolute;
          left: 0;
          right: 0;
          top: 0;
          width: 100%;
          height: 20px;
          margin: 0;
        }
        .egm-aud-range::-webkit-slider-runnable-track {
          height: 6px;
          background: transparent;
        }
        .egm-aud-range::-moz-range-track {
          height: 6px;
          background: transparent;
        }
        .egm-aud-range::-webkit-slider-thumb {
          -webkit-appearance: none;
          pointer-events: auto;
          width: 14px;
          height: 14px;
          border-radius: 9999px;
          background: #111827;
          border: 2px solid #fff;
          box-shadow: 0 0 0 1px #d1d5db;
          cursor: pointer;
          margin-top: -4px;
        }
        .egm-aud-range::-moz-range-thumb {
          pointer-events: auto;
          width: 14px;
          height: 14px;
          border-radius: 9999px;
          background: #111827;
          border: 2px solid #fff;
          box-shadow: 0 0 0 1px #d1d5db;
          cursor: pointer;
        }
      `}</style>

      <div className="flex flex-wrap items-center justify-between gap-2 gap-y-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
            <p className="text-2xl font-bold text-gray-900 tabular-nums leading-tight">
              {amountLoading ? '…' : hasData ? formatMeterRaw(amount) : '—'}
            </p>
            <p className="text-sm text-gray-600">
              {formatTradingDay(tradingDay)}
              {partialDay ? <span className="ml-1.5 text-amber-700">partial</span> : null}
              {!hasData && !amountLoading ? <span className="ml-1.5 text-gray-500">no meter data</span> : null}
            </p>
          </div>
          {allocateLabel ? <p className="text-[11px] text-gray-500 mt-0.5">{allocateLabel}</p> : null}
          {focusSubtitle ? (
            <p className="text-[11px] text-indigo-700 mt-0.5">Focused: {focusSubtitle}</p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {MACHINE_CLASS_OPTIONS.map((opt) => {
            const active = (machineClass || 'all') === opt.id
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => onMachineClassChange?.(opt.id)}
                disabled={!hasCharts && !hasData}
                title="From EGM registry DimMachine type (col 14)"
                className={`px-2 py-0.5 rounded-md text-xs font-medium border ${
                  active
                    ? 'bg-violet-700 text-white border-violet-700'
                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                } disabled:opacity-40`}
              >
                {machineClassLabel(opt, machineClassCounts)}
              </button>
            )
          })}
          <span className="w-px h-4 bg-gray-200 mx-0.5" aria-hidden />
          {METRICS.map((m) => {
            const active = m.id === metric.id
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => setMetricId(m.id)}
                disabled={!hasCharts}
                className={`px-2 py-0.5 rounded-md text-xs font-medium border ${
                  active
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                } disabled:opacity-40`}
              >
                {m.label}
              </button>
            )
          })}
          {hasData && onClear && (
            <button
              type="button"
              onClick={onClear}
              disabled={!!clearing}
              className="inline-flex items-center px-2 py-0.5 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded-md disabled:opacity-50"
            >
              <TrashIcon className="w-3.5 h-3.5 mr-1" />
              {clearing ? 'Clearing…' : 'Clear'}
            </button>
          )}
        </div>
      </div>

      <FocusFilterChips
        machineClass={machineClass}
        selectedEgmpks={selectedEgmpks}
        selectedHours={selectedHours}
        charts={charts}
        onRemoveEgm={onToggleEgm}
        onRemoveHour={onToggleHour}
        onClearFocus={handleClearFocus}
      />

      {!hasCharts ? null : (
        <>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {egmAll.length > 0 && (
              <>
                <div className="flex flex-wrap gap-1">
                  {presets.map((p) => {
                    const active = p.lo === 0 ? isAll : lo === p.lo && hiCap == null
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => applyRange(p.lo, ceiling)}
                        className={`px-1.5 py-0.5 rounded text-[11px] font-medium border ${
                          active
                            ? 'bg-gray-900 text-white border-gray-900'
                            : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
                        }`}
                      >
                        {p.label}
                      </button>
                    )
                  })}
                </div>
                <div className="flex-1 min-w-[180px]">
                  <DualAudRange
                    min={0}
                    max={ceiling}
                    lo={Math.min(lo, ceiling)}
                    hi={hi}
                    step={step}
                    color={metric.color}
                    onChange={applyRange}
                  />
                </div>
              </>
            )}
          </div>
          <p className="text-[11px] text-gray-500 mt-1.5">
            {rangeLabel} · {metric.hint}. Hover for house, game, amount.
            {typeHint ? ` · ${typeHint}` : ''}
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-3 items-stretch">
            <div className="flex flex-col min-w-0">
              <h2 className="text-xs font-semibold text-gray-800 h-4 mb-1">{metric.egmTitle}</h2>
              <div className="h-52">
                {egmRows.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={egmRows}
                      margin={{ top: 8, right: 8, left: 0, bottom: 36 }}
                      onClick={handleChartEgmClick}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 9 }}
                        interval={egmTickEvery - 1}
                        angle={-40}
                        textAnchor="end"
                        height={36}
                      />
                      <YAxis tickFormatter={audTick} tick={{ fontSize: 10 }} width={40} />
                      <Tooltip content={<ChartTooltip metric={metric} />} />
                      <Line
                        type="monotone"
                        dataKey="aud"
                        stroke={metric.color}
                        strokeWidth={2}
                        dot={(props) => {
                          const { cx, cy, payload } = props
                          if (cx == null || cy == null) return null
                          const selected = selectedEgmSet.has(String(payload?.egmpk))
                          return (
                            <circle
                              cx={cx}
                              cy={cy}
                              r={selected ? 4 : 2}
                              fill={selected ? '#312e81' : metric.color}
                              stroke={selected ? metric.color : 'none'}
                              strokeWidth={selected ? 2 : 0}
                              style={{ cursor: 'pointer' }}
                            />
                          )
                        }}
                        activeDot={{ r: 5, strokeWidth: 0 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-xs text-gray-600 py-6">
                    {egmAll.length === 0
                      ? `No ${metric.label.toLowerCase()} on any machine.`
                      : 'No machines in this range. Drag or choose All.'}
                  </p>
                )}
              </div>
            </div>

            <div className="flex flex-col min-w-0">
              <h2 className="text-xs font-semibold text-gray-800 h-4 mb-1">
                {metric.hourTitle}
                {charts.cutoff_hour != null
                  ? ` · day starts ${String(charts.cutoff_hour).padStart(2, '0')}:00`
                  : ''}
              </h2>
              <div className="h-52">
                {hasHour ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={hourRows}
                      margin={{ top: 8, right: 8, left: 0, bottom: 36 }}
                      onClick={handleChartHourClick}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                      <XAxis dataKey="label" tick={{ fontSize: 9 }} interval={1} height={36} />
                      <YAxis tickFormatter={audTick} tick={{ fontSize: 10 }} width={40} />
                      <Tooltip content={<ChartTooltip metric={metric} />} />
                      <Bar dataKey="aud" radius={[3, 3, 0, 0]} maxBarSize={18} style={{ cursor: 'pointer' }}>
                        {hourRows.map((entry) => {
                          const selected = selectedHourSet.has(entry.hour)
                          return (
                            <Cell
                              key={`hour-${entry.hour}`}
                              fill={selected ? '#115e59' : metric.color}
                            />
                          )
                        })}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-xs text-gray-600 py-6">No hourly {metric.label.toLowerCase()} for this day.</p>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

const MONTH_METRICS = [
  { id: 'turnover', label: 'Turnover', color: '#4f46e5' },
  { id: 'cashin', label: 'Cash in', color: '#0f766e' },
  { id: 'cashlessout', label: 'Ticket out', color: '#d97706' },
  { id: 'cancelcredit', label: 'Cancel credit', color: '#e11d48' },
  { id: 'carded_turnover', label: 'Carded', color: '#2563eb' },
  { id: 'uncarded_turnover', label: 'Uncarded', color: '#7c3aed' },
]

function MonthChartTooltip({ active, payload, label, metric }) {
  if (!active || !payload?.length) return null
  const row = payload[0]?.payload || {}
  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-xs shadow">
      <div className="font-medium text-gray-900">{row.fullLabel || label}</div>
      <div className="tabular-nums text-gray-800 mt-1">{formatMeterRaw(row.cents)}</div>
      <div className="text-gray-500 mt-0.5">{metric.label} · click to open day</div>
    </div>
  )
}

export function MonthTurnoverChart({ days, onSelectDay }) {
  const [metricId, setMetricId] = useState('turnover')
  const metric = MONTH_METRICS.find((m) => m.id === metricId) || MONTH_METRICS[0]

  const rows = useMemo(() => {
    const list = Array.isArray(days) ? [...days] : []
    list.sort((a, b) => String(a.trading_day).localeCompare(String(b.trading_day)))
    return list.map((row) => {
      const day = String(row.trading_day || '').slice(0, 10)
      const cents = Number(row[metric.id]) || 0
      const d = day ? new Date(`${day}T12:00:00`) : null
      const label = d && !Number.isNaN(d.getTime())
        ? d.toLocaleDateString(undefined, { day: 'numeric' })
        : day.slice(8)
      const fullLabel = d && !Number.isNaN(d.getTime())
        ? d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })
        : day
      return { day, label, fullLabel, cents, aud: toAud(cents) }
    })
  }, [days, metric.id])

  const hasData = rows.some((r) => r.aud > 0)
  if (!rows.length) return null

  return (
    <div className="mt-3">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <p className="text-xs font-semibold text-gray-800">By day</p>
        <div className="flex flex-wrap gap-1">
          {MONTH_METRICS.map((m) => {
            const active = m.id === metric.id
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => setMetricId(m.id)}
                className={`px-1.5 py-0.5 rounded text-[11px] font-medium border ${
                  active
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
                }`}
              >
                {m.label}
              </button>
            )
          })}
        </div>
      </div>
      {hasData ? (
        <div className="h-52">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={rows}
              margin={{ top: 8, right: 8, left: 0, bottom: 4 }}
              onClick={(state) => {
                const day = state?.activePayload?.[0]?.payload?.day
                if (day) onSelectDay?.(day)
              }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} />
              <YAxis tickFormatter={audTick} tick={{ fontSize: 10 }} width={44} />
              <Tooltip content={<MonthChartTooltip metric={metric} />} />
              <Bar
                dataKey="aud"
                fill={metric.color}
                radius={[3, 3, 0, 0]}
                maxBarSize={28}
                cursor="pointer"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className="text-xs text-gray-600 py-6">No {metric.label.toLowerCase()} on any day this month.</p>
      )}
    </div>
  )
}
